/**
 * Error Message Translator (v1.11.1)
 *
 * Traduce mensajes de error técnicos de WooCommerce Store API
 * a mensajes amigables para el usuario.
 *
 * WooCommerce Blocks usa React para renderizar errores, por lo que
 * los filtros PHP no siempre pueden interceptar los mensajes.
 * Esta solución JavaScript observa el DOM y traduce los mensajes
 * cuando aparecen.
 */

// Mapeo de patrones a mensajes amigables
const ERROR_TRANSLATIONS = {
    // Por campo específico (regex match en el mensaje)
    'spcb/document_type': 'Debes seleccionar Boleta o Factura.',
    'spcb/contact_phone': 'El teléfono es obligatorio.',
    'spcb/contact_first_name': 'El nombre es obligatorio.',
    'spcb/contact_last_name': 'El apellido es obligatorio.',
    'spcb/billing_rut': 'El RUT es obligatorio.',
    'spcb/shipping_rut': 'El RUT de quien recibe es obligatorio.',
    'spcb/invoice_company': 'La Razón Social es obligatoria para Factura.',
    'spcb/invoice_giro': 'El Giro es obligatorio para Factura.',
    'spcb/invoice_address': 'La dirección de facturación es obligatoria.',
    'spcb/invoice_region': 'La región de facturación es obligatoria.',
    'spcb/invoice_comuna': 'La comuna de facturación es obligatoria.',
    'spcb/invoice_phone': 'El teléfono de facturación es obligatorio.',
};

/**
 * Traduce un mensaje de error técnico a uno amigable
 * @param {string} message - Mensaje original
 * @returns {string} - Mensaje traducido o el original
 */
function translateErrorMessage(message) {
    if (!message || typeof message !== 'string') {
        return message;
    }

    // Buscar si el mensaje contiene algún campo SPCB
    for (const [fieldPattern, translation] of Object.entries(ERROR_TRANSLATIONS)) {
        if (message.includes(fieldPattern)) {
            // Verificar que es un error de tipo (no otro tipo de error)
            if (message.includes('no es del tipo') ||
                message.includes('is not of type') ||
                message.includes('string')) {
                return translation;
            }
        }
    }

    return message;
}

/**
 * Traduce los mensajes de error en un elemento del DOM
 * @param {Element} element - Elemento que contiene el mensaje
 */
function translateErrorElement(element) {
    const originalText = element.textContent;
    const translatedText = translateErrorMessage(originalText);

    if (translatedText !== originalText) {
        element.textContent = translatedText;
        console.log('[SPCB] Error traducido:', originalText, '→', translatedText);
    }
}

/**
 * Busca y traduce todos los mensajes de error visibles
 */
function translateAllVisibleErrors() {
    // Selectores para mensajes de error de WooCommerce Blocks
    const errorSelectors = [
        '.wc-block-components-notice-banner__content',
        '.wc-block-store-notice .wc-block-components-notice-banner__content div',
        '.wc-block-components-validation-error',
        '.woocommerce-error',
        '.woocommerce-message',
    ];

    errorSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(element => {
            if (element.textContent && element.textContent.includes('spcb/')) {
                translateErrorElement(element);
            }
        });
    });
}

/**
 * Configura un MutationObserver para detectar nuevos errores
 */
function setupErrorObserver() {
    // Observar cambios en el DOM para detectar nuevos errores
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Revisar nodos añadidos
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    // Revisar si el nodo mismo es un error
                    if (node.classList &&
                        (node.classList.contains('wc-block-components-notice-banner') ||
                         node.classList.contains('wc-block-store-notice'))) {
                        const contentDiv = node.querySelector('.wc-block-components-notice-banner__content div');
                        if (contentDiv && contentDiv.textContent.includes('spcb/')) {
                            translateErrorElement(contentDiv);
                        }
                    }

                    // Revisar hijos del nodo
                    const errorElements = node.querySelectorAll('.wc-block-components-notice-banner__content div');
                    errorElements.forEach(el => {
                        if (el.textContent && el.textContent.includes('spcb/')) {
                            translateErrorElement(el);
                        }
                    });
                }
            });

            // También revisar cambios de texto
            if (mutation.type === 'characterData' &&
                mutation.target.textContent &&
                mutation.target.textContent.includes('spcb/')) {
                const parent = mutation.target.parentElement;
                if (parent) {
                    translateErrorElement(parent);
                }
            }
        });
    });

    // Observar todo el body
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
    });

    console.log('[SPCB] Error translator observer iniciado');

    return observer;
}

/**
 * Inicializa el traductor de errores
 */
export function initErrorTranslator() {
    // Traducir errores existentes
    translateAllVisibleErrors();

    // Configurar observer para nuevos errores
    setupErrorObserver();

    // También traducir periódicamente (por si el observer no captura algo)
    setInterval(translateAllVisibleErrors, 1000);

    console.log('[SPCB] Error translator iniciado (v1.11.1)');
}

// Auto-inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initErrorTranslator);
} else {
    // DOM ya está listo
    initErrorTranslator();
}

// Exportar para uso en otros módulos
export { translateErrorMessage, translateAllVisibleErrors };
